import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Send, Bot, User, CheckCircle, Edit3, FileText } from "lucide-react";
import api from "../services/api";
// (No LoadingButton used in the assistant component)
import ScannerForm from "./ScannerForm";

export default function ScannerAssistant({
  onSave,
  creating,
  language: propLanguage,
  showManualOption = false,
  onSwitchToManual,
  onScannerGenerated,
  showActionButtons = true,
  onSwitchToManualFromForm,
  initialMessages = [],
  onMessagesChange,
}) {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState(
    initialMessages.length > 0 ? initialMessages : [],
  );
  const [currentInput, setCurrentInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [scannerData, setScannerData] = useState(null);
  const [showScannerForm, setShowScannerForm] = useState(false);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
    if (onMessagesChange) {
      onMessagesChange(messages);
    }
  }, [messages, onMessagesChange]);

  // Initialize conversation
  useEffect(() => {
    if (messages.length > 0) return; // Don't re-initialize if we have messages

    const initializeChat = async () => {
      setLoading(true);
      try {
        // Use propLanguage if provided, otherwise use i18n language (which should be set from company config)
        // The backend will fallback to company language if not provided
        const currentLanguage = propLanguage || i18n.language;
        console.log("ScannerAssistant: Initializing chat", {
          propLanguage,
          i18nLanguage: i18n.language,
          currentLanguage,
        });
        // Initialize without sending a user message - just get the initial greeting
        // If language is not provided, backend will use company language
        const response = await api.callScannerAssistant({
          user_input: "", // Empty input for initialization
          conversation_history: [],
          language: currentLanguage, // Can be undefined, backend will use company language
        });
        console.log("ScannerAssistant: Initial response", response);

        if (!response || !response.message) {
          throw new Error("Respuesta inválida del asistente");
        }

        setMessages([
          {
            id: 1,
            role: "assistant",
            content: response.message,
            question: response.question,
          },
        ]);
      } catch (error) {
        console.error("Error initializing scanner assistant:", error);
        let errorMsg =
          "Sorry, there was an error starting the assistant. Please try creating your scanner manually.";
        if (i18n.language === "es") {
          errorMsg =
            "Lo siento, hubo un error al iniciar el asistente. Por favor, intenta crear tu escáner manualmente.";
        } else if (i18n.language === "it") {
          errorMsg =
            "Spiacenti, si è verificato un errore durante l'avvio dell'assistente. Prova a creare lo scanner manualmente.";
        } else if (i18n.language === "pt") {
          errorMsg =
            "Desculpe, ocorreu um erro ao iniciar o assistente. Por favor, tente criar seu scanner manualmente.";
        }
        setMessages([
          {
            id: 1,
            role: "assistant",
            content: errorMsg,
            error: true,
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    initializeChat();
  }, [t, propLanguage, i18n.language, messages.length]);

  const sendMessage = async () => {
    if (!currentInput.trim() || loading) return;

    const userMessage = {
      id: Date.now(),
      role: "user",
      content: currentInput,
    };

    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);
    setCurrentInput("");

    try {
      console.log("ScannerAssistant: Sending user message", {
        userInput: userMessage.content,
        conversationHistoryLength: messages.length,
        conversationHistory: messages,
        language: propLanguage || i18n.language || "es",
      });

      // Use propLanguage if provided, otherwise use i18n language (which should be set from company config)
      // The backend will fallback to company language if not provided
      const response = await api.callScannerAssistant({
        user_input: userMessage.content,
        conversation_history: messages.map((msg) => ({
          role: msg.role,
          content: msg.content,
        })), // Send previous messages as history
        language: propLanguage || i18n.language, // Can be undefined, backend will use company language
      });

      console.log("ScannerAssistant: Received response", response);

      if (!response || !response.message) {
        throw new Error(
          `Respuesta inválida del asistente: ${JSON.stringify(response)}`,
        );
      }

      const assistantMessage = {
        id: Date.now() + 1,
        role: "assistant",
        content: response.message,
        question: response.question,
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // If scanner is complete, show it
      if (response.step === "complete" && response.scanner_data) {
        setScannerData(response.scanner_data);
        setShowScannerForm(true);
        // Notify parent component about generated scanner
        if (onScannerGenerated) {
          onScannerGenerated(response.scanner_data);
        }
      }
    } catch (error) {
      console.error("Error calling scanner assistant:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
      });

      const currentLang = propLanguage || i18n.language || "en";
      let errorMsg =
        "Sorry, there was an error. Please try creating your scanner manually.";
      if (currentLang === "es") {
        errorMsg =
          "Lo siento, hubo un error. Por favor, intenta crear tu escáner manualmente.";
      } else if (currentLang === "it") {
        errorMsg =
          "Spiacenti, si è verificato un errore. Prova a creare lo scanner manualmente.";
      } else if (currentLang === "pt") {
        errorMsg =
          "Desculpe, ocorreu um erro. Por favor, tente criar seu scanner manualmente.";
      }

      const errorMessage = {
        id: Date.now() + 1,
        role: "assistant",
        content: errorMsg,
        error: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleScannerSave = async (data) => {
    await onSave(data);
  };

  if (showScannerForm && scannerData) {
    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg sm:rounded-xl p-4 sm:p-5 md:p-6 shadow-sm">
          <div className="flex items-center gap-2 sm:gap-3">
            <CheckCircle className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-primary-500 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h3 className="text-base sm:text-lg font-semibold text-primary-700">
                {t("onboarding.scanner_generated", "¡Escáner generado con IA!")}
              </h3>
              <p className="text-primary-600 text-xs sm:text-sm mt-1">
                {t(
                  "onboarding.review_fields",
                  "Revisa y ajusta los campos según tus necesidades",
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Scanner Form */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-4 sm:p-5 md:p-6">
          <ScannerForm
            onSave={handleScannerSave}
            initial={scannerData}
            creating={creating}
            canCreate={true}
            isEditing={false}
          />
        </div>

        {/* Action buttons - only show if enabled */}
        {showActionButtons && (
          <div className="text-center space-y-2 sm:space-y-3">
            <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-3">
              <button
                onClick={() => setShowScannerForm(false)}
                className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors text-sm sm:text-base"
              >
                <Edit3 className="w-4 h-4" />
                {t("scanners.adjust_with_assistant") ||
                  "Ajustar con el asistente"}
              </button>
              {onSwitchToManualFromForm && (
                <button
                  onClick={() => onSwitchToManualFromForm(scannerData)}
                  className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors text-sm sm:text-base"
                >
                  <FileText className="w-4 h-4" />
                  {t("scanners.switch_to_manual") || "Modo Manual"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Main assistant UI
  return (
    <div className="bg-white/50 border border-white/60 rounded-xl sm:rounded-2xl overflow-hidden shadow-inner">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-emerald-600 p-3 sm:p-4 md:p-5 text-white shadow-md relative overflow-hidden">
        <div className="absolute top-0 right-0 p-2 sm:p-4 opacity-10">
          <Bot className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 transform rotate-12" />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 relative z-10">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 min-w-0 flex-1">
            <div className="bg-white/20 p-1.5 sm:p-2 rounded-lg sm:rounded-xl backdrop-blur-sm border border-white/10 flex-shrink-0">
              <Bot className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-base sm:text-lg md:text-xl font-bold truncate sm:whitespace-normal">
                {t("onboarding.scanner_assistant_title") ||
                  "Asistente IA para Plantillas"}
              </h2>
              <p className="text-primary-50 text-xs sm:text-sm font-medium opacity-90 line-clamp-2 sm:line-clamp-none">
                {t("onboarding.scanner_assistant_subtitle") ||
                  "Te ayudo a crear tu primera plantilla de extracción paso a paso"}
              </p>
            </div>
          </div>

          {/* Manual mode button - only show if option is available */}
          {showManualOption && (
            <button
              onClick={onSwitchToManual}
              className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg transition-all text-xs font-semibold backdrop-blur-md self-start sm:self-auto"
              title={t("scanners.switch_to_manual") || "Editar manualmente"}
            >
              <FileText className="w-3 h-3 flex-shrink-0" />
              <span className="hidden sm:inline">
                {t("scanners.switch_to_manual_short") || "Manual"}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Chat Messages */}
      <div
        ref={chatContainerRef}
        className="h-[300px] sm:h-[350px] md:h-[400px] lg:h-[450px] overflow-y-auto p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4 md:space-y-6 bg-gray-50/50 scroll-smooth"
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex gap-2 sm:gap-3 md:gap-4 ${
              message.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            {message.role === "assistant" && (
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-primary-100 to-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm border border-white">
                <Bot className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary-600" />
              </div>
            )}

            <div
              className={`max-w-[85%] sm:max-w-[80%] lg:max-w-[75%] px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 md:py-3.5 rounded-xl sm:rounded-2xl shadow-sm text-xs sm:text-sm leading-relaxed ${
                message.role === "user"
                  ? "bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-tr-none"
                  : message.error
                    ? "bg-red-50 border border-red-100 text-red-700 rounded-tl-none"
                    : "bg-white border border-gray-100 text-gray-700 rounded-tl-none"
              }`}
            >
              <p className="break-words">{message.content}</p>
              {message.question && (
                <p
                  className={`text-xs mt-2 italic border-t pt-2 ${
                    message.role === "user"
                      ? "border-white/20 opacity-80"
                      : "border-gray-100 text-gray-400"
                  }`}
                >
                  {message.question}
                </p>
              )}
            </div>

            {message.role === "user" && (
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" />
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex gap-2 sm:gap-3 md:gap-4 justify-start">
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-primary-50 rounded-full flex items-center justify-center flex-shrink-0 border border-primary-100">
              <Bot className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary-600" />
            </div>
            <div className="bg-white border border-gray-100 px-3 sm:px-4 py-2 sm:py-3 rounded-xl sm:rounded-2xl rounded-tl-none shadow-sm">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="flex gap-1 sm:gap-1.5">
                  <div className="w-1 sm:w-1.5 h-1 sm:h-1.5 bg-primary-400 rounded-full animate-bounce"></div>
                  <div
                    className="w-1 sm:w-1.5 h-1 sm:h-1.5 bg-primary-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="w-1 sm:w-1.5 h-1 sm:h-1.5 bg-primary-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>
                <span className="text-xs text-gray-400 font-medium ml-1">
                  {t("onboarding.thinking", "Thinking...")}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="bg-white p-3 sm:p-4 border-t border-gray-100">
        <div className="relative flex items-center gap-1.5 sm:gap-2 bg-gray-50 border border-gray-200 rounded-lg sm:rounded-xl p-1 sm:p-1.5 focus-within:ring-2 focus-within:ring-primary-500/20 focus-within:border-primary-500 transition-all shadow-inner">
          <input
            type="text"
            value={currentInput}
            onChange={(e) => setCurrentInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={
              t("onboarding.type_message") || "Escribe tu respuesta aquí..."
            }
            className="flex-1 bg-transparent border-none px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 text-gray-700 placeholder-gray-400 text-xs sm:text-sm focus:ring-0"
            disabled={loading}
            autoFocus
          />

          <button
            onClick={sendMessage}
            disabled={!currentInput.trim() || loading}
            className="p-2 sm:p-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white shadow-md disabled:opacity-50 disabled:shadow-none transition-all transform hover:scale-105 active:scale-95 flex-shrink-0"
            aria-label={t("onboarding.send_message", "Send message")}
          >
            {loading ? (
              <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            )}
          </button>
        </div>

        <div className="mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-400 text-center font-medium flex items-center justify-center gap-1 sm:gap-1.5">
          <span className="w-1 h-1 rounded-full bg-gray-300"></span>
          <span>{t("onboarding.press_enter", "Press Enter to send")}</span>
          <span className="w-1 h-1 rounded-full bg-gray-300"></span>
        </div>
      </div>
    </div>
  );
}
